import { getPica } from './pica';

export interface CompressImageOptions {
  /** Maximum width in pixels the compressed image may reach. Aspect ratio preserved. */
  maxWidth?: number;
  /** Maximum height in pixels the compressed image may reach. Aspect ratio preserved. */
  maxHeight?: number;
  /** Pica resize quality (0–3). Higher is better but slower. Default 3. */
  quality?: 0 | 1 | 2 | 3;
  /** Output MIME type. Defaults to the input file's type. */
  outputType?: string;
  /** Hard cap for a single image in ms. Default 30000. */
  timeoutMs?: number;
}

const DEFAULT_MAX_DIMENSION = 1920;

/**
 * Generous enough that a huge photo on a slow device finishes normally, but
 * short enough that a wedged decode or worker cannot hold the queue hostage.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

const formatMegabytes = (bytes: number) =>
  `${(bytes / 1024 / 1024).toFixed(1)}MB`;

const getScaledDimensions = (
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
) => {
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }
  const ratio = Math.min(maxWidth / originalWidth, maxHeight / originalHeight);
  return {
    width: Math.max(1, Math.round(originalWidth * ratio)),
    height: Math.max(1, Math.round(originalHeight * ratio))
  };
};

const compressImageNow = async (
  file: File,
  options: CompressImageOptions
): Promise<File> => {
  const {
    maxWidth = DEFAULT_MAX_DIMENSION,
    maxHeight = DEFAULT_MAX_DIMENSION,
    quality = 3,
    outputType,
    timeoutMs = DEFAULT_TIMEOUT_MS
  } = options;

  const type = outputType || file.type;
  const startedAt = performance.now();
  let imageBitmap: ImageBitmap | null = null;
  let abandoned = false;
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined;

  // Rejects once the budget is spent. Pica accepts it as `cancelToken` and
  // terminates a running resize; the races below cover the steps that have no
  // cancellation of their own. Every step must settle, because a single
  // never-settling promise would stall every queued compression after it.
  const deadline = new Promise<never>((_resolve, reject) => {
    deadlineTimer = setTimeout(
      () => reject(new Error(`compressImage timed out after ${timeoutMs}ms`)),
      timeoutMs
    );
  });
  // The race may already be over when the timer fires — keep that rejection
  // from surfacing as an unhandled one.
  deadline.catch(() => undefined);

  try {
    // Inside the try so that a browser unable to construct Pica falls back to
    // the original file instead of rejecting and stalling the queue.
    const pica = getPica();

    const decoding = createImageBitmap(file);
    // If the deadline wins the race below, `finally` has already run and
    // nothing else would ever release a bitmap that shows up late.
    decoding.then(
      (bitmap) => {
        if (abandoned) bitmap.close?.();
      },
      () => undefined
    );
    imageBitmap = await Promise.race([decoding, deadline]);
    const { width, height } = getScaledDimensions(
      imageBitmap.width,
      imageBitmap.height,
      maxWidth,
      maxHeight
    );

    const targetCanvas = new OffscreenCanvas(width, height);

    // The bitmap goes to pica as-is. Copying it into a source canvas first
    // would allocate a second width * height * 4 buffer, and browsers cap
    // canvas area (~268M pixels in Chrome, far less in Safari) — so a large
    // photo failed outright at that allocation. Pica reads an ImageBitmap
    // tile by tile and leaves ownership with us, so `finally` still releases
    // it. `cancelToken` stops the actual work — pica aborts its tile loop and
    // stops waiting on the worker. Racing on top of it guarantees that this
    // step settles even if pica ever failed to honour the token.
    await Promise.race([
      pica.resize(imageBitmap, targetCanvas, {
        quality,
        cancelToken: deadline
      }),
      deadline
    ]);

    const blob: Blob = await Promise.race([
      // @ts-ignore — OffscreenCanvas.convertToBlob is not in every lib.dom variant
      targetCanvas.convertToBlob({ type }) as Promise<Blob>,
      deadline
    ]);

    const compressed = new File([blob], file.name, {
      type,
      lastModified: Date.now()
    });

    // Re-encoding can grow a file. The source may have been saved at a lower
    // JPEG quality than the browser's default for convertToBlob, so a
    // detailed photo can come out bigger despite having far fewer pixels.
    // Compressing must never make the upload worse than not compressing.
    if (compressed.size >= file.size) {
      console.info(
        `compressImage: ${file.name} ${formatMegabytes(file.size)} ` +
          `${imageBitmap.width}x${imageBitmap.height} → kept original ` +
          `(re-encode produced ${formatMegabytes(compressed.size)} ` +
          `at ${width}x${height})`
      );
      return file;
    }

    // One line per image, so a support case can be read straight off the
    // console. An output size equal to the input means compression fell back
    // to the original — the failure branch below says why.
    console.info(
      `compressImage: ${file.name} ${formatMegabytes(file.size)} ` +
        `${imageBitmap.width}x${imageBitmap.height} → ` +
        `${formatMegabytes(compressed.size)} ${width}x${height} ` +
        `(${Math.round(performance.now() - startedAt)}ms)`
    );

    return compressed;
  } catch (error) {
    // Name the input and how far we got: "not decoded" points at
    // createImageBitmap, real dimensions point at the resize or the encode.
    const source = imageBitmap
      ? `${imageBitmap.width}x${imageBitmap.height}px`
      : 'not decoded';
    console.error(
      `compressImage failed for "${file.name}" (${formatMegabytes(
        file.size
      )}, ${source}), returning original file:`,
      error
    );
    return file;
  } finally {
    abandoned = true;
    clearTimeout(deadlineTimer);
    // Decoded bitmaps hold width * height * 4 bytes outside the JS heap until
    // the GC gets around to them. Releasing them here keeps peak memory flat
    // across a batch instead of growing with every file.
    imageBitmap?.close?.();
  }
};

// Promise<void> documents that the tail must not carry a value. It is not
// enforced here — this project builds with strictNullChecks disabled, so
// `Promise<File | undefined>` still assigns cleanly. The comment below is the
// actual guard.
let queue: Promise<void> = Promise.resolve();

/**
 * Run compressions strictly one after another.
 *
 * Callers fan out over their files (`Promise.all`, upload loops), which would
 * otherwise decode every image at once — a 24MP photo costs ~96MB as a decoded
 * bitmap, so a 20-image selection peaked high enough to kill the tab. Queueing
 * caps the peak at a single bitmap. Throughput barely suffers because a single
 * `pica.resize()` already spreads its tiles across the whole worker pool.
 */
const enqueue = async <T>(task: () => Promise<T>): Promise<T> => {
  const result = queue.then(task, task);
  // Discard both outcomes. `.catch()` alone would pass the fulfilled File
  // through, and this queue is module-global — the last processed file would
  // stay reachable for the rest of the session, which is worst exactly when
  // the fallback returned a huge original.
  queue = result.then(
    () => undefined,
    () => undefined
  );
  return await result;
};

/**
 * Compress and resize an image `File` in the browser using Pica.
 *
 * Aspect ratio is preserved, and images already within the target dimensions
 * are not upscaled. Compressions are serialised across the whole app to keep
 * peak memory flat. On any failure the original file is returned so callers
 * can safely wrap this in an upload pipeline without extra try/catch.
 */
export const compressImage = async (
  file: File,
  options: CompressImageOptions = {}
): Promise<File> =>
  await enqueue(async () => await compressImageNow(file, options));

const IMAGE_MIME_PREFIX = 'image/';

/** True when a `File` is an image type that `compressImage` should handle. */
export const isCompressibleImage = (file: File): boolean =>
  file.type.startsWith(IMAGE_MIME_PREFIX);

/**
 * Convenience wrapper for the common "run compressImage on every image in a
 * batch, pass through anything else" pattern used by upload flows.
 */
export const compressImages = async (
  files: File[],
  options?: CompressImageOptions
): Promise<File[]> =>
  Promise.all(
    files.map((file) =>
      isCompressibleImage(file) ? compressImage(file, options) : file
    )
  );
